/**
 * ElementRegistry — Maps element types to VFX palettes, particle presets,
 * and default on-hit effects.
 *
 * Some elements supply a default on-hit status when an ability has no
 * explicit hit effects. Delivery still depends on the behavior and an
 * admitted server hit; a school alone does not promise an applied status.
 */

import { VFX_PALETTE } from '../../../../../shared/constants/VFXPalettes';
import { applyBurn, applySlow, applyPoison } from '../modifiers/HitEffects';
import type { ElementConfig, ElementType } from '../modifiers/types';

export const ELEMENTS: Record<ElementType, ElementConfig> = {
  fire: {
    type: 'fire',
    palette: { primary: VFX_PALETTE.mage.fire, accent: 0xFFDD44, trail: 0xFF6600 },
    particlePreset: 'preset_fire_trail',
    defaultOnHit: applyBurn(3, 3000),
    trailVfxId: 'preset_fire_trail',
  },
  frost: {
    type: 'frost',
    palette: { primary: VFX_PALETTE.mage.frost, accent: VFX_PALETTE.mage.frostLight, trail: 0x88DDFF },
    particlePreset: 'preset_frost_mist',
    defaultOnHit: applySlow(0.3, 2000),
    trailVfxId: 'preset_frost_mist',
  },
  shadow: {
    type: 'shadow',
    palette: { primary: VFX_PALETTE.rogue.shadow, accent: VFX_PALETTE.rogue.shadowDark, trail: VFX_PALETTE.rogue.shadowDeep },
    particlePreset: 'preset_shadow_wisps',
    defaultOnHit: applyPoison({ damage: 2, duration: 4000 }),
  },
  holy: {
    type: 'holy',
    palette: { primary: VFX_PALETTE.paladin.holy, accent: VFX_PALETTE.paladin.holyBright, trail: VFX_PALETTE.paladin.holyWhite },
    particlePreset: 'preset_holy_radiance',
  },
  arcane: {
    type: 'arcane',
    palette: { primary: VFX_PALETTE.mage.arcane, accent: VFX_PALETTE.mage.arcaneLight, trail: 0xDD88FF },
    particlePreset: 'preset_arcane_sparks',
  },
  nature: {
    type: 'nature',
    palette: { primary: VFX_PALETTE.hunter.nature, accent: VFX_PALETTE.hunter.poison, trail: 0x88FF88 },
    particlePreset: 'preset_poison_cloud',
    defaultOnHit: applyPoison({ damage: 2, duration: 3000 }),
  },
  lightning: {
    type: 'lightning',
    palette: { primary: 0xFFFF00, accent: 0xFFFFFF, trail: 0x4488FF },
    particlePreset: 'preset_electric_crackle',
  },
  physical: {
    type: 'physical',
    palette: { primary: VFX_PALETTE.warrior.steel, accent: VFX_PALETTE.warrior.steelDark, trail: VFX_PALETTE.warrior.dust },
    particlePreset: 'preset_blood_splatter',
  },
};

// Register on globalThis for lazy circular-dep access from AbilityBuilder
import { setAbilityElements } from '../../../../core/HelixGlobals';
setAbilityElements(ELEMENTS);
